import React, { useState, useEffect, useCallback } from "react";
import flightIcon from "../assets/svg2/flyWHITE.svg";
import bedIcon from "../assets/svg2/bedWHITE.svg";
import styled from 'styled-components';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useStoreFly } from "../store";
import { useFilters } from "../hooks/useFilters";
import { LookupItems } from "./common/LookupItems";
import { DrawerComponent } from "./common/Drawer";
import { Modal } from "./ModalTransitions";
import { NewDatePicker } from './common/NewDatePicker';
import { getPreferredAirport } from "../hooks/getPreferredAirports";
import { useGeolocation } from "../hooks/useGeolocation";
import { searchAirports, debounce } from "../utils/airportSearchNew";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SwapHorizontalCircleIcon from '@mui/icons-material/SwapHorizontalCircle';
import SearchIcon from '@mui/icons-material/Search';
import { getCountryCode } from "../utils/countryCodeLookup";
import { SELECTED_CLASS } from "../utils/enums";
import { nanoid } from "nanoid";
import { CookieConsent } from './common/CookieConsent';
import moment from "moment";
// Import SVG icons from assets
import departureSVG from '../assets/svg2/departure.svg';
import arrivalSVG from '../assets/svg2/arrival.svg';
import personOutlineSVG from '../assets/svg/personOutLine.svg';

// Add CSS override for search button color
const searchButtonStyle = document.createElement('style');
searchButtonStyle.textContent = `
  .banner .btn-submit.mobile-search-btn {
    background: #fbd215 !important;
    opacity: 1 !important;
    font-weight: 700 !important;
  }
  
  .banner .btn-submit.mobile-search-btn:disabled {
    background: #fbd215 !important;
    filter: none !important;
    opacity: 1 !important;
    font-weight: 700 !important;
  }
`;
if (!document.head.querySelector('style[data-search-button]')) {
  searchButtonStyle.setAttribute('data-search-button', 'true');
  document.head.appendChild(searchButtonStyle);
}

// Add CSS animation for loading spinner
const spinnerStyle = document.createElement('style');
spinnerStyle.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
if (!document.head.querySelector('style[data-spinner]')) {
  spinnerStyle.setAttribute('data-spinner', 'true');
  document.head.appendChild(spinnerStyle);
}

// Update CSS for consistent input styling
const spacingFixStyle = document.createElement('style');
spacingFixStyle.textContent = `
  /* Force tabs to be positioned below logo */
  .banner .nav-tabs {
    margin-top: 100px !important;
  }
  
  /* Mobile date picker full screen fixes */
  .mobile-date-picker {
    width: 100% !important;
    max-width: 100vw !important;
    height: auto !important;
    overflow: hidden !important;
  }
  
  /* Ensure consistent spacing between inputs */
  .banner .form-group {
    margin-bottom: 21px !important;
  }
  
  /* Ensure drawer takes full screen on mobile */
  @media (max-width: 768px) {
    .MuiDrawer-paper {
      width: 100vw !important;
      height: 100vh !important;
      max-width: 100% !important;
      max-height: 100% !important;
    }
    
    .react-datepicker-wrapper {
      width: 100% !important;
    }
    
    .react-datepicker {
      width: 100% !important;
      max-width: 100% !important;
  }
  }
  
  /* Remove text decorations from tabs */
  .banner .nav-tabs li a {
    text-decoration: none !important;
  }
  
  .banner .nav-tabs li a:hover,
  .banner .nav-tabs li a:focus,
  .banner .nav-tabs li a:active {
    text-decoration: none !important;
  }

  /* Remove Bootstrap column padding */
  .banner .row {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  
  .banner .col-lg-6,
  .banner .col-md-6,
  .banner .col-12 {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
  
  /* Remove problematic margins and spacing */
  .banner .ftinput .form-group::before {
    display: none !important;
  }
  
  .banner .ftinput .form-group,
  .banner .ltinput .form-group {
    margin-left: 0 !important;
    margin-right: 0 !important;
    margin-bottom: 0 !important;
    width: 100% !important;
  }
  
  /* Ensure inputs take full width with consistent font styling */
  .banner .form-control.flight1,
  .banner .form-control.flight2 {
    width: 100% !important;
    margin: 0 !important;
    padding-left: 66px !important;
    padding-right: 16px !important;
    box-sizing: border-box !important;
    font-size: 18px !important;
    font-weight: 500 !important;
  }
  
  /* Unified spacing for the bottom inputs (date picker, travellers, search) */
  @media (max-width: 768px) {
    .banner .bottom-container {
      display: flex !important;
      flex-direction: column !important;
      width: 100% !important;
      margin-top: 2px !important; /* ultra minimal space above date picker */
      gap: 8px !important; /* balanced vertical gap between date, travellers, search */
    }

    /* Remove internal margins now that we use gap */
    .banner .bottom-container .form-group {
      margin: 0 !important;
    }

    .banner .bottom-container input.form-control,
    .banner .bottom-container .btn-submit {
      margin: 0 !important;
    }
  }
  
  /* Consistent input height and styling for mobile */
  .banner input[type="search"],
  .banner input[type="text"],
  .banner .form-control {
    height: 56px !important;
    font-size: 18px !important;
    font-weight: 500 !important;
    line-height: 1.2 !important;
  }
  
  /* Ensure proper spacing for custom SVG icons */
  .banner .form-control.flight1,
  .banner .form-control.flight2 {
    padding-left: 50px !important;
  }
  
  /* Button styling consistency */
  .banner .btn-submit {
    font-size: 18px !important;
    font-weight: 600 !important;
    height: 56px !important;
  }

  .banner .ltinput .form-group {
    margin-bottom: 8px !important; /* space below destination input */
  }

  /* Travelers input specific styling */
  .banner .form-control.bg-user {
    padding-left: 50px !important;
    font-size: 18px !important;
    font-weight: 500 !important;
    text-align: left !important;
    text-indent: 0 !important;
    color: #003663 !important; /* Ensure consistent brand color */
  }
`;
if (!document.head.querySelector('style[data-spacing-fix]')) {
  spacingFixStyle.setAttribute('data-spacing-fix', 'true');
  document.head.appendChild(spacingFixStyle);
}

// Strongest override specifically for travellers input weight
const travelerWeightStyle = document.createElement('style');
travelerWeightStyle.textContent = `
  input.form-control.bg-user {
    font-weight: 500 !important;
  }
`;
travelerWeightStyle.setAttribute('data-traveler-weight', 'true');
document.head.appendChild(travelerWeightStyle);

// Move mobile logo further to the left
const logoLeftFixStyle = document.createElement('style');
logoLeftFixStyle.textContent = `
  @media (max-width: 768px) {
    .header .navbar-brand {
      margin-left: 23px !important;
      padding-left: 0 !important;
    }
  }
`;
if (!document.head.querySelector('style[data-logo-left-fix]')) {
  logoLeftFixStyle.setAttribute('data-logo-left-fix', 'true');
  document.head.appendChild(logoLeftFixStyle);
}

const BannerNewMobile = ({ translation, showSearchBar = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { geolocation } = useGeolocation();
  const setFilter = useStoreFly((state) => state.setFilter);
  
  // Move all hooks to the beginning before any conditional returns
  // Basic state
  const [currentStep, setCurrentStep] = useState(1);
  const [originValue, setOriginValue] = useState('');
  const [originCode, setOriginCode] = useState('');
  const [originCity, setOriginCity] = useState('');
  const [destinationValue, setDestinationValue] = useState('');
  const [destinationCode, setDestinationCode] = useState('');
  const [destinationCity, setDestinationCity] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [options, setOptions] = useState({
    adult: 1,
    children: 0,
    babies: 0,
    class: "Economy"
  });
  const [returnOrOneWay, setReturnOrOneWay] = useState('return');

  // Search state
  const [originSearchResults, setOriginSearchResults] = useState([]);
  const [destinationSearchResults, setDestinationSearchResults] = useState([]);
  const [isOriginSearching, setIsOriginSearching] = useState(false);
  const [isDestinationSearching, setIsDestinationSearching] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeDrawer, setActiveDrawer] = useState(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState(0);

  // UI state (moved from after conditional returns)
  const [viewOriginLookup, setViewOriginLookup] = useState(false);
  const [viewDestinationLookup, setViewDestinationLookup] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [travellerDropDownShow, setTravellerDropDownShow] = useState(false);

  // Prevent geo-prefill from re-applying after user edits the field
  const [originPrefilled, setOriginPrefilled] = useState(false);

  // If we are on the mobile results page we should not auto-advance to the next step after each selection
  const isResultsPage = location.pathname.includes('/search');

  // All useCallback and useEffect hooks
  const debouncedOriginSearch = useCallback(
    debounce(async (searchTerm) => {
      if (!searchTerm || searchTerm.length < 3) {
        setOriginSearchResults([]);
        setIsOriginSearching(false);
        return;
      }

      try {
        setIsOriginSearching(true);
        const results = await searchAirports(searchTerm);
        setOriginSearchResults(results);
      } catch (error) {
        // Origin search error
        setOriginSearchResults([]);
      } finally {
        setIsOriginSearching(false);
      }
    }, 300),
    []
  );

  const debouncedDestinationSearch = useCallback(
    debounce(async (searchTerm) => {
      if (!searchTerm || searchTerm.length < 3) {
        setDestinationSearchResults([]);
        setIsDestinationSearching(false);
        return;
      }

      try {
        setIsDestinationSearching(true);
        const results = await searchAirports(searchTerm);
        setDestinationSearchResults(results);
      } catch (error) {
        // Destination search error
        setDestinationSearchResults([]);
      } finally {
        setIsDestinationSearching(false);
      }
    }, 300),
    []
  );

  // Effect: pick nearest/preferred airport from user geo and pre-fill origin input
  useEffect(() => {
    const prefillOriginWithGeo = async () => {
      if (!geolocation || originValue || originPrefilled) return;

        try {
        const countryCode = getCountryCode(geolocation.city) || geolocation.city; // fall back to raw value

        const preferredAirport = getPreferredAirport(countryCode, {
          latitude: geolocation.latitude,
          longitude: geolocation.longitude,
        });

          if (preferredAirport) {
          const formattedValue = `${preferredAirport.city_name} - ${preferredAirport.name}`;

          setOriginValue(formattedValue);
          setOriginCode(preferredAirport.id);
          setOriginCity(formattedValue);

          // Persist in global filter so further components know
          setFilter("filterRoute", "origin", preferredAirport);
          setOriginPrefilled(true);
          }
        } catch (error) {
        console.error("Prefill origin airport failed:", error);
      }
    };

    prefillOriginWithGeo();
  }, [geolocation, originValue, setFilter, originPrefilled]);

  // Initialize dates
  useEffect(() => {
    if (!dateRange) {
      const getNextWeekend = () => {
        const today = new Date();
        const currentDay = today.getDay();
        const daysUntilFriday = (5 - currentDay + 7) % 7;
        const friday = new Date(today);
        friday.setDate(today.getDate() + daysUntilFriday);
        const sunday = new Date(friday);
        sunday.setDate(friday.getDate() + 2);
        return {
          startDate: friday,
          endDate: sunday,
          key: 'selection'
        };
      };

      const nextWeekend = getNextWeekend();
      setDateRange(nextWeekend);
    }
  }, [dateRange]);

  // Cookie consent initialization
  useEffect(() => {
    const initCookieConsent = () => {
      if (window.gtag) {
        window.gtag('consent', 'default', {
          'ad_storage': 'denied',
          'analytics_storage': 'denied'
        });
      }
    };
    initCookieConsent();
  }, []);

  // Set next weekend as default dates
  useEffect(() => {
    if (!dateRange) {
      const getNextWeekend = () => {
        const today = new Date();
        const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        
        // Calculate days until next Saturday
        const daysUntilSaturday = currentDay === 6 ? 7 : (6 - currentDay);
        
        const nextSaturday = new Date(today);
        nextSaturday.setDate(today.getDate() + daysUntilSaturday);
        
        const nextSunday = new Date(nextSaturday);
        nextSunday.setDate(nextSaturday.getDate() + 1);
        
        return { saturday: nextSaturday, sunday: nextSunday };
      };

      const weekend = getNextWeekend();
      
      // Use default dates similar to Popular component
      const defaultStartDate = moment().add(10, 'days').toDate();
      const defaultEndDate = moment().add(15, 'days').toDate();
      
      if (returnOrOneWay === 'return') {
        setDateRange({
          startDate: defaultStartDate,
          endDate: defaultEndDate
        });
      } else {
        setDateRange({
          startDate: defaultStartDate,
          endDate: null
        });
      }
    }
  }, [returnOrOneWay, dateRange]);

  // Update date range when trip type changes
  useEffect(() => {
    if (dateRange && returnOrOneWay === 'oneway' && dateRange.endDate) {
      // Clear end date for one-way trips
      setDateRange({
        startDate: dateRange.startDate,
        endDate: null
      });
    }
  }, [returnOrOneWay, dateRange]);

  const handleDateChange = (newDateRange) => {
    setDateRange(newDateRange);
    // ✅ MOBILE CHANGE: No longer auto-advance steps on date selection
    // Step advancement now happens only when DONE button is clicked
  };

  // ✅ NEW: Handle mobile DONE button click for step advancement
  const handleMobileDateDone = (dateRange) => {
    if (returnOrOneWay === 'oneway' && dateRange?.startDate) {
      if (!isResultsPage) {
        setCurrentStep(4);
        // Auto-open travelers dropdown
        setTimeout(() => {
          setTravellerDropDownShow(true);
        }, 100);
      }
    } else if (returnOrOneWay === 'return' && dateRange?.startDate && dateRange?.endDate) {
      if (!isResultsPage) {
        setCurrentStep(4);
        // Auto-open travelers dropdown
        setTimeout(() => {
          setTravellerDropDownShow(true);
        }, 100);
      }
    }
  };

  const handleCalendarClose = () => {
    setIsCalendarOpen(false);
  };

  const handleSwapLocations = () => {
    const tempValue = originValue;
    const tempCode = originCode;
    const tempCity = originCity;
    
    setOriginValue(destinationValue);
    setOriginCode(destinationCode);
    setOriginCity(destinationCity);
    
    setDestinationValue(tempValue);
    setDestinationCode(tempCode);
    setDestinationCity(tempCity);
  };

  const handleOption = (name, operation) => {
    setOptions(prev => {
      const newValue = operation === 'plus' ? prev[name] + 1 : prev[name] - 1;
      
      // Apply limits
      if (name === 'adult' && newValue < 1) return prev;
      if (name === 'adult' && newValue > 9) return prev;
      if ((name === 'children' || name === 'babies') && newValue < 0) return prev;
      if ((name === 'children' || name === 'babies') && newValue > 9) return prev;
      
      return {
        ...prev,
        [name]: newValue
      };
    });
  };

  const handleSearchClick = () => {
    // Format dates properly
    const formattedDateFrom = dateRange?.startDate ? moment(dateRange.startDate).format("DD/MM/YYYY") : null;
    const formattedDateTo = dateRange?.endDate ? moment(dateRange.endDate).format("DD/MM/YYYY") : null;
    
    // Validate required fields
    if (!originCode || !destinationCode || !formattedDateFrom) {
      console.error("Missing required search fields:", { originCode, destinationCode, formattedDateFrom });
      return;
    }

    const searchData = {
      flyFrom: originCode,
      flyTo: destinationCode,
      cityNameFrom: originCity,
      cityNameTo: destinationCity,
      originCode: originCode,
      destinationCode: destinationCode,
      returnOrOneWay,
      dateFrom: formattedDateFrom,
      dateTo: returnOrOneWay === "return" ? formattedDateTo : null,
      adults: options.adult,
      babies: options.babies,
      children: options.children,
      infants: options.babies,
      selectedClass: SELECTED_CLASS[options.class] || "M",
      vehicleType: "aircraft",
      limit: 30,
      selectedAirlines: [],
      aTimeFrom: "0:00",
      aTimeTo: "24:00",
      dtimeFrom: "0:00",
      dtimeTo: "24:00",
      retAtimeFrom: null,
      retAtimeTo: null,
      retDtimeFrom: null,
      retDtimeTo: null,
      returnFromDate: returnOrOneWay === "return" ? formattedDateTo : null,
      returnToDate: returnOrOneWay === "return" ? formattedDateTo : null,
      curr: "EUR",
    };

            // Mobile search data being processed
    localStorage.setItem("search", JSON.stringify(searchData));
    navigate(`/search?session=${nanoid()}`);
  };

  // 🔹 Auto-prefill fields with previous search (mobile)
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("search") || "{}");
      if (!stored || Object.keys(stored).length === 0) return;

      // Origin
      if (!originCode && stored.flyFrom) {
        const originDisplay = stored.cityNameFrom || stored.flyFrom;
        setOriginCode(stored.flyFrom);
        setOriginCity(originDisplay);
        setOriginValue(originDisplay);
      }

      // Destination
      if (!destinationCode && stored.flyTo) {
        const destDisplay = stored.cityNameTo || stored.flyTo;
        setDestinationCode(stored.flyTo);
        setDestinationCity(destDisplay);
        setDestinationValue(destDisplay);
      }

      // Dates
      if (stored.dateFrom) {
        const start = moment(stored.dateFrom, "DD/MM/YYYY").toDate();
        const end = stored.dateTo ? moment(stored.dateTo, "DD/MM/YYYY").toDate() : null;
        setDateRange({ startDate: start, endDate: end, key: "selection" });
      }

      // Pax & class
      if (stored.adults || stored.children || stored.babies) {
        setOptions((prev) => ({
          ...prev,
          adult: stored.adults || prev.adult,
          children: stored.children || prev.children,
          babies: stored.babies || prev.babies
        }));
      }
      if (stored.selectedClass) {
        setReturnOrOneWay(stored.returnOrOneWay || 'return');
      }
    } catch (err) {
      console.warn('Could not prefill mobile banner from stored search', err);
    }
  }, []);

  // Safety check for translation function
  if (!translation || typeof translation !== 'function') {
    console.error('Translation function is not provided or is not a function:', translation);
    return (
      <section className="banner" style={{ 
        minHeight: "120px", 
        background: "#003663", 
        paddingBottom: "20px",
        position: "relative"
      }}>
        <div style={{
          height: "60px", // Space below logo
          backgroundColor: "#003663"
        }}>
        </div>
      </section>
    );
  }
  
  // If showSearchBar is false, render only the blue header
  if (!showSearchBar) {
    return (
      <>
        <style>{`
          @media (max-width: 768px) {
            .header .navbar-brand {
              margin-left: -15px !important;
              padding-left: 15px !important;
              margin-bottom: 20px !important;
            }
            
            .header .navbar {
              padding-bottom: 25px !important;
            }
            
            .header .container {
              padding-left: 5px !important;
            }
          }
        `}</style>
        <section className="banner" style={{ 
          minHeight: "120px", 
          background: "#003663", 
          paddingBottom: "30px",
          position: "relative",
          marginTop: "10px"
        }}>
          <div style={{
            height: "80px", // Increased space below logo
            backgroundColor: "#003663"
          }}>
          </div>
        </section>
      </>
    );
  }
  
  // Input handlers
  const handleOriginInputChange = async (event) => {
    const value = event.target.value;
    setOriginValue(value);
    setFilter("filterRoute", "originInput", value);
    setIsOriginSearching(true);
    // User is modifying manually – disable future auto-prefill
    if (!originPrefilled) setOriginPrefilled(true);
    debouncedOriginSearch(value);
  };

  const handleDestinationInputChange = async (event) => {
    const value = event.target.value;
    setIsDestinationSearching(true);
    setDestinationValue(value);
    setFilter("filterRoute", "destinationInput", value);
    debouncedDestinationSearch(value);
  };

  return (
    <section className="banner" style={{ minHeight: "auto", background: "#034C88", paddingBottom: "40px" }}>
      <div className="container">
        <div className="row">
          <div className="col-lg-12 bot-minus col-md-12 col-12">
            <ul className="nav nav-tabs" style={{ paddingLeft: "16px" }}>
              <li>
                <Link to="/" className="active" style={{display:'flex',alignItems:'center',gap:'6px'}}>
                  <img src={flightIcon} alt="Flight"  style={{ width: '26px', height: '26px' }} />
                  <span>{translation("flights")}</span>
                </Link>
              </li>
              <li>
                <Link to="/hotels" style={{display:'flex',alignItems:'center',gap:'6px'}}>
                  <img src={bedIcon} alt="Hotel" style={{ width: '26px', height: '26px' }} />
                  <span>{translation("hotels")}</span>
                </Link>
              </li>
            </ul>
            
            <div className="tab-content">
              <div
                id="tab-1"
                className="tab-pane fade in show active"
                style={{
                  paddingTop: "15px",
                  minHeight: "55px",
                }}
              >
                {/* Trip Type Radio Buttons */}
                <div className="col-lg-12 col-md-12 col-12">
                  <div className="col-lg-12 radiobg p-0 col-md-12 col-12" style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "8px",
                    padding: "0",
                    display: "flex"
                  }}>
                    <div className="custom-control custom-radio">
                      <input
                        type="radio"
                        id="return"
                        name="customRadio"
                        className="custom-control-input return-radio"
                        value="return"
                        checked={returnOrOneWay === "return"}
                        onChange={() => setReturnOrOneWay("return")}
                      />
                      <label 
                        className="custom-control-label" 
                        htmlFor="return"
                        style={{
                          backgroundColor: "transparent",
                          color: returnOrOneWay === "return" ? "#ffffff" : "#003663",
                          borderTopLeftRadius: "8px",
                          borderBottomLeftRadius: "8px",
                          borderTopRightRadius: "0px",
                          borderBottomRightRadius: "0px"
                        }}
                      >
            {translation("return")}
                      </label>
                    </div>
                    <div className="custom-control custom-radio">
                      <input
                        type="radio"
                        id="oneway"
                        name="customRadio"
                        className="custom-control-input oneway-radio"
                        value="oneway"
                        checked={returnOrOneWay === "oneway"}
                        onChange={() => setReturnOrOneWay("oneway")}
                      />
                      <label 
                        className="custom-control-label" 
                        htmlFor="oneway"
                        style={{
                          backgroundColor: "transparent",
                          color: returnOrOneWay === "oneway" ? "#ffffff" : "#003663",
                          borderTopRightRadius: "8px",
                          borderBottomRightRadius: "8px",
                          borderTopLeftRadius: "0px",
                          borderBottomLeftRadius: "0px"
                        }}
                      >
            {translation("one_way")}
                      </label>
                    </div>
                  </div>
                </div>

                {/* Mobile Search Inputs */}
                <div className="row">
                  <div className="col-lg-6 ftinput col-md-6 col-12">
                    <div className="form-group" style={{ marginLeft: "0", marginRight: "0", position: 'relative' }}>
                      <div style={{
                        position: 'absolute',
                        left: '16px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                        pointerEvents: 'none'
                      }}>
                        <img 
                          src={departureSVG} 
                          alt="Departure" 
                          style={{ 
                            width: '24px', 
                            height: '24px'
                          }} 
                        />
                      </div>
                      <input
                        type="search"
                        className="form-control flight1 custom-dropdown"
                        value={originValue}
                        onClick={() => {
                          setCurrentStep(1);
                          setViewOriginLookup(true);
                          setViewDestinationLookup(false);
                          setTravellerDropDownShow(false);
                        }}
                        onChange={handleOriginInputChange}
                        style={{
                          caretColor: "transparent",
                          borderBottomLeftRadius: "0",
                          borderBottomRightRadius: "0",
                          borderBottom: "1px dashed #e0e0e0",
                          paddingLeft: "60px",
                          background: "#fff"
                        }}
                        placeholder={translation("origin")}
                      />
                </div>
              </div>

                  {/* Swap Button */}
                  <div 
                    className="col-lg-12 col-md-12 col-12 d-flex justify-content-center"
                    style={{ 
                      position: "relative", 
                      height: "0",
                      zIndex: 10
                    }}
                  >
                    <span
                      className="swap-icon"
                      style={{
                        position: "absolute",
                        top: "-18px",
                        right: "2vw",
                        background: "#ffffff",
                        border: "2px solid #003663",
                        borderRadius: "50%",
                        width: "36px",
                        height: "36px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        zIndex: 10,
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                      }}
                      onClick={handleSwapLocations}
                    >
                      <svg 
                        width="20" 
                        height="20" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        style={{ transform: "rotate(90deg)" }}
                      >
                        <path 
                          d="M7 11L3 15L7 19V16H15V14H7V11ZM21 9L17 5V8H9V10H17V13L21 9Z" 
                          fill="#003663"
                          strokeWidth="0.5"
                          stroke="#003663"
                        />
                      </svg>
                    </span>
                  </div>

                  {/* Destination Input */}
                  <div className="col-lg-6 ltinput col-md-6 col-12">
                    <div className="form-group" style={{ marginLeft: "0", marginRight: "0", position: 'relative' }}>
                      <div style={{
                        position: 'absolute',
                        left: '16px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                        pointerEvents: 'none'
                      }}>
                        <img 
                          src={arrivalSVG} 
                          alt="Arrival" 
                          style={{ 
                            width: '24px', 
                            height: '24px'
                          }} 
                        />
                      </div>
                      <input
                        type="text"
                        className="form-control flight2 custom-dropdown"
                        value={destinationValue}
                        onClick={() => {
                          setCurrentStep(2);
                          setViewDestinationLookup(true);
                        }}
                        onChange={handleDestinationInputChange}
                        style={{
                          caretColor: "transparent",
                          borderTopLeftRadius: "0",
                          borderTopRightRadius: "0",
                          paddingLeft: "60px",
                          background: "#fff",
                          fontWeight: 500
                        }}
                        placeholder={translation("destination")}
                      />
                    </div>
                  </div>

                  {/* Bottom Inputs Container */}
                  <div className="col-12 bottom-container">

                    {/* Date Picker Input */}
                    <div className="col-lg-6 col-md-6 col-12">
                      <div className="form-group">
                        <div 
                          className="form-control date-picker date-picker-flight"
                          onClick={() => {
                            setCurrentStep(3);
                            setIsCalendarOpen(true);
                          }}
                          style={{
                            caretColor: "transparent",
                            paddingLeft: "60px",
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                            color: dateRange?.startDate ? "#003663" : "#999",
                            fontWeight: dateRange?.startDate ? "600" : "400",
                            position: "relative",
                            background: "#fff",
                          }}
                        >
                          <CalendarTodayIcon style={{
                            position: 'absolute',
                            left: '16px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#003663',
                            fontSize: '20px',
                            pointerEvents: 'none'
                          }} />
                {dateRange?.startDate 
                          ? returnOrOneWay === 'oneway'
                            ? new Date(dateRange.startDate).toLocaleDateString('en-GB')
                            : `${new Date(dateRange.startDate).toLocaleDateString('en-GB')}${dateRange.endDate ? ' - ' + new Date(dateRange.endDate).toLocaleDateString('en-GB') : ''}`
                          : translation("select_dates")
                        }
              </div>
            </div>

                    {/* Travelers Input */}
                    </div>
                    <div className="col-lg-6 col-md-6 col-12">
                      <div className="form-group" style={{ position: 'relative' }}>
                        <div style={{
                          position: 'absolute',
                          left: '16px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 2,
                          pointerEvents: 'none'
                        }}>
                          <img 
                            src={personOutlineSVG} 
                            alt="Travelers" 
                            style={{ 
                              width: '24px', 
                              height: '24px'
                            }} 
                          />
                        </div>
                        <input
                          type="text"
                          className="form-control bg-user"
                          onClick={() => {
                            setCurrentStep(4);
                            setTravellerDropDownShow(true);
                          }}
                          value={`${
                            Number(options.adult) +
                            Number(options.children) +
                            Number(options.babies)
                          } ${translation("traveler")}${
                            options.adult > 1 || options.children || options.babies
                              ? translation("s")
                              : ""
                          } | ${options.class}`}
                          style={{
                            caretColor: "transparent",
                            paddingLeft: "60px",
                            background: "#fff",
                            fontWeight: 500,
                            color: "#003663"
                          }}
                          readOnly
                        />
              </div>
            </div>

                    {/* Search Button */}
                    <div className="col-lg-3 col-md-3 col-12">
                      <div className="form-group">
                        <button
                          type="button"
                          className="btn-submit mobile-search-btn"
                          onClick={handleSearchClick}
                          disabled={!originCode || !destinationCode || !dateRange?.startDate}
                          style={{
                            width: "100%",
                            height: "60px",
                            backgroundColor: '#FBD215',
                            color: '#003663',
                            border: 'none',
                            borderRadius: '16px',
                            fontSize: '22px',
                            fontWeight: 'bold',
                            cursor: (!originCode || !destinationCode || !dateRange?.startDate) ? 'not-allowed' : 'pointer',
                            boxShadow: '0px 4px 8px rgba(0,0,0,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            filter: (!originCode || !destinationCode || !dateRange?.startDate) ? 'brightness(0.8)' : 'none'
                          }}
                          onMouseEnter={(e) => {
                            if (originCode && destinationCode && dateRange?.startDate) {
                              e.currentTarget.style.backgroundColor = '#e6be14';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (originCode && destinationCode && dateRange?.startDate) {
                              e.currentTarget.style.backgroundColor = '#FBD215';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }
                          }}
                        >
                          {translation("search")}
                          <img 
                            src={require("../assets/img/icon-search.png")} 
                            alt="Search" 
                            style={{ width: '20px', height: '20px' }}
                          />
                        </button>
                      </div>
                    </div>

                  </div> {/* end bottom-container */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Origin Lookup Modal */}
      <Modal isOpen={currentStep === 1 && viewOriginLookup} onClose={() => setViewOriginLookup(false)}>
        <DrawerComponent
          id="origin"
          open={viewOriginLookup}
          setOpen={setViewOriginLookup}
          headerText={translation("select_origin")}
          anchor="bottom"
        >
          <div style={{ padding: "10px", position: "relative" }}>
            <div style={{ position: "relative" }}>
              <input
                type="search"
                className="form-control flight1 custom-dropdown"
              value={originValue}
                autoFocus={true}
              onChange={handleOriginInputChange}
                placeholder={translation("origin")}
                style={{ marginBottom: "10px" }}
              />
              {/* Airport search results */}
              {isOriginSearching ? (
                <div style={{ 
                  padding: "24px", 
                  color: "#6B7280", 
                  textAlign: "center",
                  background: "white",
                  borderRadius: "16px",
                  margin: "8px 0",
                  boxShadow: "0 8px 32px rgba(0, 54, 99, 0.12)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "12px"
                }}>
                  <div style={{
                    width: "32px",
                    height: "32px",
                    border: "3px solid rgba(0, 54, 99, 0.1)",
                    borderTop: "3px solid #003663",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite"
                  }}></div>
                  <span style={{ fontSize: "16px", fontWeight: "500" }}>Searching airports...</span>
                </div>
              ) : originSearchResults.length > 0 ? (
                <div style={{ 
                  margin: "8px 0",
                  background: "white",
                  borderRadius: "16px",
                  border: "none",
                  maxHeight: "65vh", // Increased height based on viewport
                  overflowY: "auto",
                  boxShadow: "0 8px 32px rgba(0, 54, 99, 0.12)",
                  WebkitOverflowScrolling: 'touch' // Smooth scrolling on iOS
                }}>
                  {originSearchResults.map((airport, index) => (
                    <div
                      key={airport.id || index}
                      style={{
                        padding: "16px 20px",
                        borderBottom: index < originSearchResults.length - 1 ? "1px solid rgba(0, 54, 99, 0.08)" : "none",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        position: "relative"
                      }}
                      onClick={() => {
                        const formattedValue = `${airport.city_name} - ${airport.name}`;
                setOriginCity(formattedValue);
                setOriginValue(formattedValue);
                        setOriginCode(airport.id);
                        
                setViewOriginLookup(false);
                if (!isResultsPage) {
                  setCurrentStep(2);
                  setViewDestinationLookup(true);
                }
              }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(0, 54, 99, 0.04)';
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}
                    >
                      {/* Airport icon */}
                      <div style={{
                        width: "40px",
                        height: "40px",
                        backgroundColor: "rgba(0, 54, 99, 0.08)",
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M21 16V14L13 9V3.5C13 2.67 12.33 2 11.5 2S10 2.67 10 3.5V9L2 14V16L10 13.5V19L8 20.5V22L11.5 21L15 22V20.5L13 19V13.5L21 16Z" fill="#003663"/>
                        </svg>
                      </div>
                      
                      {/* Airport info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          fontWeight: "600", 
                          color: "#003663",
                          fontSize: "16px",
                          lineHeight: "1.3",
                          marginBottom: "2px"
                        }}>
                          {airport.city_name} - {airport.name}
                        </div>
                        <div style={{ 
                          fontSize: "14px", 
                          color: "#6B7280",
                          lineHeight: "1.2",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}>
                          <span style={{
                            backgroundColor: "rgba(0, 54, 99, 0.1)",
                            color: "#003663",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            fontSize: "11px",
                            fontWeight: "600",
                            letterSpacing: "0.5px"
                          }}>
                            {airport.id}
                          </span>
                          <span>{airport.country_code}</span>
                        </div>
                      </div>
                      
                      {/* Arrow icon */}
                      <div style={{
                        color: "#9CA3AF",
                        transition: "all 0.2s ease"
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              ) : originValue.length >= 2 ? (
                <div style={{ 
                  padding: "24px", 
                  color: "#6B7280", 
                  textAlign: "center",
                  background: "white",
                  borderRadius: "16px",
                  margin: "8px 0",
                  boxShadow: "0 8px 32px rgba(0, 54, 99, 0.12)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "12px"
                }}>
                  <div style={{
                    width: "48px",
                    height: "48px",
                    backgroundColor: "rgba(239, 68, 68, 0.1)",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#EF4444"/>
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontSize: "16px", fontWeight: "600", color: "#374151", marginBottom: "4px" }}>
                      No airports found
                    </div>
                    <div style={{ fontSize: "14px" }}>
                      Try searching for "{originValue.substring(0, 20)}"
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            <li
              className="d-block d-lg-none d-md-none"
              style={{ padding: "0px 18px" }}
            >
              <button
                type="submit"
                className="btn-submit"
                onClick={() => {
                  setViewOriginLookup(false);
                  setCurrentStep(2);
                  setViewDestinationLookup(true);
                }}
              >
                {translation("done")}
              </button>
            </li>
          </div>
        </DrawerComponent>
      </Modal>

      {/* Destination Lookup Modal */}
      <Modal isOpen={currentStep === 2 && viewDestinationLookup} onClose={() => setViewDestinationLookup(false)}>
        <DrawerComponent
          id="destination"
          open={viewDestinationLookup}
          headerText={translation("select_destination")}
          anchor="bottom"
          setOpen={setViewDestinationLookup}
        >
          <div style={{ padding: "10px", position: "relative" }}>
            <div style={{ position: "relative" }}>
              <input
                type="search"
                className="form-control flight1 custom-dropdown"
              value={destinationValue}
              onChange={handleDestinationInputChange}
                autoFocus={true}
                placeholder={translation("destination")}
                style={{ marginBottom: "10px" }}
              />
              {/* Airport search results */}
              {isDestinationSearching ? (
                <div style={{ 
                  padding: "24px", 
                  color: "#6B7280", 
                  textAlign: "center",
                  background: "white",
                  borderRadius: "16px",
                  margin: "8px 0",
                  boxShadow: "0 8px 32px rgba(0, 54, 99, 0.12)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "12px"
                }}>
                  <div style={{
                    width: "32px",
                    height: "32px",
                    border: "3px solid rgba(0, 54, 99, 0.1)",
                    borderTop: "3px solid #003663",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite"
                  }}></div>
                  <span style={{ fontSize: "16px", fontWeight: "500" }}>Searching airports...</span>
                </div>
              ) : destinationSearchResults.length > 0 ? (
                <div style={{ 
                  margin: "8px 0",
                  background: "white",
                  borderRadius: "16px",
                  border: "none",
                  maxHeight: "65vh", // Increased height based on viewport
                  overflowY: "auto",
                  boxShadow: "0 8px 32px rgba(0, 54, 99, 0.12)",
                  WebkitOverflowScrolling: 'touch' // Smooth scrolling on iOS
                }}>
                  {destinationSearchResults.map((airport, index) => (
                    <div
                      key={airport.id || index}
                      style={{
                        padding: "16px 20px",
                        borderBottom: index < destinationSearchResults.length - 1 ? "1px solid rgba(0, 54, 99, 0.08)" : "none",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        position: "relative"
                      }}
                      onClick={() => {
                        setDestinationCity(airport.city_name + ' - ' + airport.name);
                        setDestinationValue(airport.city_name + ' - ' + airport.name);
                        setDestinationCode(airport.id);
                        setFilter("filterRoute", "destination", airport);
                        
                setViewDestinationLookup(false);
                if (!isResultsPage) {
                  setCurrentStep(3);
                  setIsCalendarOpen(true);
                }
              }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(0, 54, 99, 0.04)';
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}
                    >
                      {/* Airport icon */}
                      <div style={{
                        width: "40px",
                        height: "40px",
                        backgroundColor: "rgba(0, 54, 99, 0.08)",
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M21 16V14L13 9V3.5C13 2.67 12.33 2 11.5 2S10 2.67 10 3.5V9L2 14V16L10 13.5V19L8 20.5V22L11.5 21L15 22V20.5L13 19V13.5L21 16Z" fill="#003663"/>
                        </svg>
                      </div>
                      
                      {/* Airport info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          fontWeight: "600", 
                          color: "#003663",
                          fontSize: "16px",
                          lineHeight: "1.3",
                          marginBottom: "2px"
                        }}>
                          {airport.city_name} - {airport.name}
                        </div>
                        <div style={{ 
                          fontSize: "14px", 
                          color: "#6B7280",
                          lineHeight: "1.2",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}>
                          <span style={{
                            backgroundColor: "rgba(0, 54, 99, 0.1)",
                            color: "#003663",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            fontSize: "11px",
                            fontWeight: "600",
                            letterSpacing: "0.5px"
                          }}>
                            {airport.id}
                          </span>
                          <span>{airport.country_code}</span>
                        </div>
                      </div>
                      
                      {/* Arrow icon */}
                      <div style={{
                        color: "#9CA3AF",
                        transition: "all 0.2s ease"
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              ) : destinationValue.length >= 2 ? (
                <div style={{ 
                  padding: "24px", 
                  color: "#6B7280", 
                  textAlign: "center",
                  background: "white",
                  borderRadius: "16px",
                  margin: "8px 0",
                  boxShadow: "0 8px 32px rgba(0, 54, 99, 0.12)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "12px"
                }}>
                  <div style={{
                    width: "48px",
                    height: "48px",
                    backgroundColor: "rgba(239, 68, 68, 0.1)",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#EF4444"/>
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontSize: "16px", fontWeight: "600", color: "#374151", marginBottom: "4px" }}>
                      No airports found
                    </div>
                    <div style={{ fontSize: "14px" }}>
                      Try searching for "{destinationValue.substring(0, 20)}"
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            <li
              className="d-block d-lg-none d-md-none"
              style={{ padding: "0px 18px" }}
            >
              <button
                type="submit"
                className="btn-submit"
                onClick={() => {
                  setViewDestinationLookup(false);
                  setCurrentStep(3);
                  setIsCalendarOpen(true);
                }}
              >
                {translation("done")}
              </button>
            </li>
          </div>
        </DrawerComponent>
      </Modal>

      {/* Date Picker Modal */}
      <Modal isOpen={isCalendarOpen} onClose={handleCalendarClose}>
        <DrawerComponent
          id="date-picker"
          open={isCalendarOpen}
          setOpen={handleCalendarClose}
          headerText={translation("select_dates")}
          anchor="bottom"
          style={{
            height: "100vh",
            width: "100vw",
            margin: 0,
            padding: 0,
            maxWidth: "100%",
            maxHeight: "100%",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <div style={{ 
            padding: "8px", 
            height: "calc(100vh - 60px)", 
            width: "100%",
            display: "flex", 
            flexDirection: "column",
            overflow: "hidden",
            boxSizing: "border-box"
          }}>
          <NewDatePicker
              isRange={returnOrOneWay === 'return'}
            value={dateRange}
            onChange={handleDateChange}
              onOpenChange={handleCalendarClose}
            onDoneClick={handleMobileDateDone}
            minDate={new Date()}
            isOpen={true}
            preventDirectOpen={false}
            isMobile={true}
              customHeight="48px"
              departurePlaceholder={translation("departure_date")}
              returnPlaceholder={translation("return_date")}
              placeholder={translation("select_date")}
              containerClassName="mobile-date-picker"
          />
          </div>
        </DrawerComponent>
      </Modal>

      {/* Travelers Modal */}
      <Modal isOpen={currentStep === 4 && travellerDropDownShow} onClose={() => setTravellerDropDownShow(false)}>
        <DrawerComponent
          id="passenger"
          headerText={translation("passenger_and_class")}
          anchor="bottom"
          open={travellerDropDownShow}
          setOpen={setTravellerDropDownShow}
        >
          <div className="col-lg-6 col-md-6 col-12">
            <div className="form-group">
              <div className="traveller-dropdown p-class">
                <ul className="list-group" style={{ marginTop: "85px" }}>
                  <li>
                    <div className="list-group-item d-flex justify-content-between align-items-center">
                      <span className="nme">
                        <div style={{ 
                          color: "#003663", 
                          fontSize: "16px", 
                          fontWeight: "500",
                          lineHeight: "1.2"
                        }}>{translation("adults")}</div>
                        <div style={{ 
                          color: "#6B7280", 
                          fontSize: "14px",
                          lineHeight: "1.2"
                        }}>{translation("12_years_and_older")}</div>
                      </span>
                      <span className="quantity">
                <button 
                  disabled={options.adult <= 1}
                          onClick={() => handleOption("adult", "minus")}
                          style={{
                            width: "32px",
                            height: "32px",
                            minWidth: "32px",
                            minHeight: "32px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "18px",
                            cursor: options.adult <= 1 ? "not-allowed" : "pointer",
                            border: "none",
                            borderRadius: "50%",
                            backgroundColor: options.adult <= 1 ? "#e9ecef" : "#FBD316",
                            color: "#003663",
                            transition: "all 0.2s ease",
                            transform: "scale(1)",
                            padding: "0",
                            lineHeight: "1",
                            boxSizing: "border-box",
                            opacity: options.adult <= 1 ? 0.5 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (options.adult > 1) {
                              e.currentTarget.style.transform = "scale(1.2)";
                              e.currentTarget.style.backgroundColor = "#e6be14";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (options.adult > 1) {
                              e.currentTarget.style.transform = "scale(1)";
                              e.currentTarget.style.backgroundColor = "#FBD316";
                            }
                          }}
                >
                  -
                </button>
                        <input
                          data-value="true"
                          type="text"
                          readOnly=""
                          value={options.adult}
                          disabled
                        />
                <button 
                          onClick={() => handleOption("adult", "plus")}
                          disabled={options.adult >= 6}
                          style={{
                            width: "32px",
                            height: "32px",
                            minWidth: "32px",
                            minHeight: "32px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "18px",
                            cursor: options.adult >= 6 ? "not-allowed" : "pointer",
                            border: "none",
                            borderRadius: "50%",
                            backgroundColor: options.adult >= 6 ? "#e9ecef" : "#FBD316",
                            color: "#003663",
                            transition: "all 0.2s ease",
                            transform: "scale(1)",
                            padding: "0",
                            lineHeight: "1",
                            boxSizing: "border-box",
                            opacity: options.adult >= 6 ? 0.5 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (options.adult < 6) {
                              e.currentTarget.style.transform = "scale(1.2)";
                              e.currentTarget.style.backgroundColor = "#e6be14";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (options.adult < 6) {
                              e.currentTarget.style.transform = "scale(1)";
                              e.currentTarget.style.backgroundColor = "#FBD316";
                            }
                          }}
                >
                  +
                </button>
                      </span>
              </div>
                  </li>
                  <li>
                    <div className="list-group-item d-flex justify-content-between align-items-center">
                      <span className="nme">
                        <div style={{ 
                          color: "#003663", 
                          fontSize: "16px", 
                          fontWeight: "500",
                          lineHeight: "1.2"
                        }}>{translation("children")}</div>
                        <div style={{ 
                          color: "#6B7280", 
                          fontSize: "14px",
                          lineHeight: "1.2"
                        }}>{translation("2_to_11_years")}</div>
                      </span>
                      <span className="quantity">
                <button 
                  disabled={options.children <= 0}
                          onClick={() => handleOption("children", "minus")}
                          style={{
                            width: "32px",
                            height: "32px",
                            minWidth: "32px",
                            minHeight: "32px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "18px",
                            cursor: options.children <= 0 ? "not-allowed" : "pointer",
                            border: "none",
                            borderRadius: "50%",
                            backgroundColor: options.children <= 0 ? "#e9ecef" : "#FBD316",
                            color: "#003663",
                            transition: "all 0.2s ease",
                            transform: "scale(1)",
                            padding: "0",
                            lineHeight: "1",
                            boxSizing: "border-box",
                            opacity: options.children <= 0 ? 0.5 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (options.children > 0) {
                              e.currentTarget.style.transform = "scale(1.2)";
                              e.currentTarget.style.backgroundColor = "#e6be14";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (options.children > 0) {
                              e.currentTarget.style.transform = "scale(1)";
                              e.currentTarget.style.backgroundColor = "#FBD316";
                            }
                          }}
                >
                  -
                </button>
                        <input
                          data-value="true"
                          type="text"
                          readOnly=""
                          value={options.children}
                          disabled
                        />
                <button 
                          onClick={() => handleOption("children", "plus")}
                          disabled={options.children >= 4}
                          style={{
                            width: "32px",
                            height: "32px",
                            minWidth: "32px",
                            minHeight: "32px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "18px",
                            cursor: options.children >= 4 ? "not-allowed" : "pointer",
                            border: "none",
                            borderRadius: "50%",
                            backgroundColor: options.children >= 4 ? "#e9ecef" : "#FBD316",
                            color: "#003663",
                            transition: "all 0.2s ease",
                            transform: "scale(1)",
                            padding: "0",
                            lineHeight: "1",
                            boxSizing: "border-box",
                            opacity: options.children >= 4 ? 0.5 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (options.children < 4) {
                              e.currentTarget.style.transform = "scale(1.2)";
                              e.currentTarget.style.backgroundColor = "#e6be14";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (options.children < 4) {
                              e.currentTarget.style.transform = "scale(1)";
                              e.currentTarget.style.backgroundColor = "#FBD316";
                            }
                          }}
                >
                  +
                </button>
                      </span>
              </div>
                  </li>
                  <li>
                    <div className="list-group-item d-flex justify-content-between align-items-center">
                      <span className="nme">
                        <div style={{ 
                          color: "#003663", 
                          fontSize: "16px", 
                          fontWeight: "500",
                          lineHeight: "1.2"
                        }}>{translation("babies")}</div>
                        <div style={{ 
                          color: "#6B7280", 
                          fontSize: "14px",
                          lineHeight: "1.2"
                        }}>{translation("under_2_years_old")}</div>
                      </span>
                      <span className="quantity">
                <button 
                  disabled={options.babies <= 0}
                          onClick={() => handleOption("babies", "minus")}
                          style={{
                            width: "32px",
                            height: "32px",
                            minWidth: "32px",
                            minHeight: "32px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "18px",
                            cursor: options.babies <= 0 ? "not-allowed" : "pointer",
                            border: "none",
                            borderRadius: "50%",
                            backgroundColor: options.babies <= 0 ? "#e9ecef" : "#FBD316",
                            color: "#003663",
                            transition: "all 0.2s ease",
                            transform: "scale(1)",
                            padding: "0",
                            lineHeight: "1",
                            boxSizing: "border-box",
                            opacity: options.babies <= 0 ? 0.5 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (options.babies > 0) {
                              e.currentTarget.style.transform = "scale(1.2)";
                              e.currentTarget.style.backgroundColor = "#e6be14";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (options.babies > 0) {
                              e.currentTarget.style.transform = "scale(1)";
                              e.currentTarget.style.backgroundColor = "#FBD316";
                            }
                          }}
                >
                  -
                </button>
                        <input
                          data-value="true"
                          type="text"
                          readOnly=""
                          value={options.babies}
                          disabled
                        />
                <button 
                          onClick={() => handleOption("babies", "plus")}
                          disabled={options.babies >= 4}
                          style={{
                            width: "32px",
                            height: "32px",
                            minWidth: "32px",
                            minHeight: "32px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "18px",
                            cursor: options.babies >= 4 ? "not-allowed" : "pointer",
                            border: "none",
                            borderRadius: "50%",
                            backgroundColor: options.babies >= 4 ? "#e9ecef" : "#FBD316",
                            color: "#003663",
                            transition: "all 0.2s ease",
                            transform: "scale(1)",
                            padding: "0",
                            lineHeight: "1",
                            boxSizing: "border-box",
                            opacity: options.babies >= 4 ? 0.5 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (options.babies < 4) {
                              e.currentTarget.style.transform = "scale(1.2)";
                              e.currentTarget.style.backgroundColor = "#e6be14";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (options.babies < 4) {
                              e.currentTarget.style.transform = "scale(1)";
                              e.currentTarget.style.backgroundColor = "#FBD316";
                            }
                          }}
                >
                  +
                </button>
                      </span>
              </div>
                  </li>

                  {/* Class Selection */}
                  <li className="custom-control mt-sp custom-radio normal-radio">
                    <input
                      type="radio"
                      id="customRadio-val-1"
                      name="classRadio"
                      className="custom-control-input"
                      value="Economy"
                      checked={options.class === "Economy"}
                      onChange={() => setOptions(prev => ({ ...prev, class: "Economy" }))}
                    />
                    <label
                      className="custom-control-label"
                      htmlFor="customRadio-val-1"
                      style={
                        options.class === "Economy"
                          ? { background: "#003663", color: "#fff" }
                          : {}
                      }
                    >
                      {translation("Economy")}
                    </label>
                  </li>
                  <li className="custom-control custom-radio normal-radio">
                    <input
                      type="radio"
                      id="customRadio-val-2"
                      name="classRadio"
                      value="Premium Economy"
                      onChange={() => setOptions(prev => ({ ...prev, class: "Premium Economy" }))}
                      checked={options.class === "Premium Economy"}
                      className="custom-control-input"
                    />
                    <label
                      className="custom-control-label"
                      htmlFor="customRadio-val-2"
                      style={
                        options.class === "Premium Economy"
                          ? { background: "#003663", color: "#fff" }
                          : {}
                      }
                    >
                      {translation("Premium Economy")}
                    </label>
                  </li>
                  <li className="custom-control custom-radio normal-radio">
                    <input
                      type="radio"
                      id="customRadio-val-3"
                      value="Business"
                      onChange={() => setOptions(prev => ({ ...prev, class: "Business" }))}
                      checked={options.class === "Business"}
                      name="classRadio"
                      className="custom-control-input"
                    />
                    <label
                      className="custom-control-label"
                      htmlFor="customRadio-val-3"
                      style={
                        options.class === "Business"
                          ? { background: "#003663", color: "#fff" }
                          : {}
                      }
                    >
                      {translation("Business")}
                    </label>
                  </li>

                  <li className="d-block d-lg-none d-md-none">
                  <button
                      type="submit"
                      className="btn-submit"
                      onClick={() => {
                        setTravellerDropDownShow(false);
                      }}
                      style={{
                        fontSize: '16px',
                        fontWeight: '600'
                      }}
                  >
                      {translation("done")}
                  </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </DrawerComponent>
      </Modal>

      {/* Blur background when modals are open */}
      {(viewDestinationLookup || viewOriginLookup || travellerDropDownShow || isCalendarOpen) && (
        <div className="blurr2"></div>
      )}
      
      {/* Cookie Consent Banner */}
      <CookieConsent />
    </section>
  );
};

export default BannerNewMobile; 